import type { Category, Payment } from '@klopotun/contracts';
import { formatCivilDate } from '@klopotun/domain';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useCategoriesQuery } from '../../entities/category/index.js';
import { useMeQuery } from '../../entities/me/index.js';
import { useOccurrencesQuery } from '../../entities/occurrence/index.js';
import { nextDueDate, usePaymentsQuery } from '../../entities/payment/index.js';
import { useSummaryQuery, type YearSummary } from '../../entities/summary/index.js';
import { occurrencePayPath, paymentDetailPath, ROUTE } from '../../features/navigation/index.js';
import {
  earliestDueDateByPayment,
  formatPaidTotal,
  paidTotalsByPayment,
} from '../../features/occurrence-view/index.js';
import {
  formatPaymentAmount,
  formatStoredDateForPeople,
} from '../../features/payment-view/index.js';
import { formatMoneyMaybeApproximate } from '../../features/summary-view/index.js';
import {
  EmptyState,
  ErrorState,
  Fab,
  List,
  ListItem,
  PageHeader,
  Screen,
  SegmentedControl,
  Spinner,
  type SegmentedOption,
} from '../../shared/ui/index.js';
import styles from './payments-page.module.css';
import { UpcomingList } from './upcoming-list/upcoming-list.js';

type PaymentScope = 'upcoming' | 'live' | 'archive';

const SCOPE_OPTIONS: SegmentedOption[] = [
  { value: 'upcoming', label: 'Найближчі' },
  { value: 'live', label: 'Підписки' },
  { value: 'archive', label: 'Архів' },
];

const scopeOf = (value: string): PaymentScope => {
  if (value === 'live') {
    return 'live';
  }

  if (value === 'archive') {
    return 'archive';
  }

  return 'upcoming';
};

const NEW_NOTE = { label: 'нова', tone: 'accent' } as const;

const yearlyCostTextOf = (summary: YearSummary | undefined): string | null => {
  if (summary === undefined || summary.annualCost.paymentCount === 0) {
    return null;
  }

  const { annualCost } = summary;
  const isApproximate = annualCost.isConverted || annualCost.hasEstimates;
  const amount = formatMoneyMaybeApproximate(
    annualCost.totalMinor,
    summary.baseCurrency,
    isApproximate,
  );

  return `Підписки на рік: ${amount}`;
};

const categoryLabelOf = (category: Category | undefined): string | null => {
  if (category === undefined) {
    return null;
  }

  return category.emoji === null ? category.name : `${category.emoji} ${category.name}`;
};

const joinParts = (parts: (string | null)[]): string | undefined => {
  const kept = parts.filter((part): part is string => part !== null);

  return kept.length === 0 ? undefined : kept.join(' · ');
};

const subtitleOf = (
  payment: Payment,
  categoryById: Map<string, Category>,
  dueDate: string | null,
): string | undefined => {
  const category = payment.categoryId === null ? undefined : categoryById.get(payment.categoryId);

  return joinParts([
    categoryLabelOf(category),
    dueDate === null ? null : formatStoredDateForPeople(dueDate),
  ]);
};

type PaymentsListProps = {
  payments: Payment[] | undefined;
  categories: Category[];
  dueDateOf: (payment: Payment) => string | null;
  /** A quieter line under the amount - what a payment has cost so far, in the archive. */
  valueNoteOf: (payment: Payment) => string | undefined;
  isPending: boolean;
  errorMessage: string | null;
  isArchive: boolean;
  onRetry: () => void;
  onOpen: (id: string) => void;
};

const PaymentsList = ({
  payments,
  categories,
  dueDateOf,
  valueNoteOf,
  isPending,
  errorMessage,
  isArchive,
  onRetry,
  onOpen,
}: PaymentsListProps) => {
  if (errorMessage !== null) {
    return (
      <ErrorState
        title="Не вдалося завантажити підписки"
        message={errorMessage}
        onRetry={onRetry}
      />
    );
  }

  if (isPending || payments === undefined) {
    return (
      <div className={styles.centered}>
        <Spinner label="Завантаження" />
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <EmptyState
        title={isArchive ? 'Архів порожній' : 'Підписок ще немає'}
        description={
          isArchive
            ? 'Сюди потрапляють скасовані підписки — історія лишається з ними.'
            : 'Додайте першу підписку, і ми нагадаємо, коли настане платіж.'
        }
      />
    );
  }

  const categoryById = new Map(categories.map((category) => [category.id, category] as const));

  return (
    <List>
      {payments.map((payment) => (
        <ListItem
          key={payment.id}
          title={payment.name}
          subtitle={subtitleOf(payment, categoryById, dueDateOf(payment))}
          value={formatPaymentAmount(payment)}
          note={payment.isNew ? NEW_NOTE : undefined}
          valueNote={valueNoteOf(payment)}
          chevron
          onClick={() => {
            onOpen(payment.id);
          }}
        />
      ))}
    </List>
  );
};

export const PaymentsPage = () => {
  const [scope, setScope] = useState<PaymentScope>('upcoming');
  const navigate = useNavigate();
  const me = useMeQuery();
  // One status per request is the whole filter the payments API offers (design note C10), and a
  // period still waiting to be paid can belong to a payment of any status: cancelling a payment
  // leaves the periods already overdue alone, because they are real unpaid debts (design note D3).
  const livePayments = usePaymentsQuery();
  const archivedPayments = usePaymentsQuery({ status: 'cancelled' });
  const plannedPeriods = useOccurrencesQuery({ status: 'planned' });
  const summary = useSummaryQuery();
  // Every confirmed period ever, for "what this payment cost in all" beside each archived one.
  // Fetched only while the archive is open: it is the one listing that grows without bound.
  const paidPeriods = useOccurrencesQuery({ status: 'paid' }, { enabled: scope === 'archive' });
  const categories = useCategoriesQuery({ includeArchived: true });

  const listedPayments = scope === 'archive' ? archivedPayments : livePayments;
  const paymentById = new Map(
    [...(livePayments.data ?? []), ...(archivedPayments.data ?? [])].map(
      (payment) => [payment.id, payment] as const,
    ),
  );
  const plannedDueByPayment = earliestDueDateByPayment(plannedPeriods.data ?? []);
  const paidTotals = paidTotalsByPayment(paidPeriods.data ?? []);
  const timeZone = me.data?.settings.timezone;
  // Only the two requests the list cannot be drawn without. The names of the payments are what
  // the other two carry, and UpcomingList already knows how to draw a row without a name - an
  // archive listing that failed should not blank out periods that are loaded and sitting in cache.
  const upcomingFailure = plannedPeriods.error ?? me.error;
  const yearlyCostText = yearlyCostTextOf(summary.data);

  /**
   * Materialised periods stop at the horizon, so a payment repeating less often than that has no
   * row ahead of it for most of the year. The template still knows the date.
   */
  const dueDateOf = (payment: Payment): string | null => {
    const planned = plannedDueByPayment.get(payment.id);

    if (planned !== undefined) {
      return planned;
    }

    if (timeZone === undefined) {
      return null;
    }

    const forecast = nextDueDate(payment, new Date(), timeZone);

    return forecast === null ? null : formatCivilDate(forecast);
  };

  const valueNoteOf = (payment: Payment): string | undefined => {
    if (scope !== 'archive') {
      return undefined;
    }

    return formatPaidTotal(paidTotals.get(payment.id)) ?? undefined;
  };

  return (
    <Screen>
      <div className={styles.content}>
        <PageHeader title="Підписки" />
        {yearlyCostText !== null && <p className={styles.yearlyCost}>{yearlyCostText}</p>}
        <SegmentedControl
          label="Що показувати"
          name="payment-scope"
          options={SCOPE_OPTIONS}
          value={scope}
          hideLabel
          onChange={(value) => {
            setScope(scopeOf(value));
          }}
        />
        {scope === 'upcoming' ? (
          <UpcomingList
            occurrences={plannedPeriods.data}
            paymentById={paymentById}
            settings={me.data?.settings}
            isPending={
              plannedPeriods.isPending ||
              me.isPending ||
              livePayments.isPending ||
              archivedPayments.isPending
            }
            errorMessage={upcomingFailure === null ? null : upcomingFailure.message}
            onRetry={() => {
              void plannedPeriods.refetch();
              void me.refetch();
              void livePayments.refetch();
              void archivedPayments.refetch();
            }}
            onOpen={(occurrence) => {
              void navigate(paymentDetailPath(occurrence.paymentId));
            }}
            onPay={(occurrence) => {
              void navigate(occurrencePayPath(occurrence.id));
            }}
          />
        ) : (
          <>
            {categories.isError && (
              <p className={styles.listWarning} role="alert">
                Категорії не завантажилися — назви не показуємо
              </p>
            )}
            {plannedPeriods.isError && (
              <p className={styles.listWarning} role="alert">
                Періоди не завантажилися — найближчі дати не показуємо
              </p>
            )}
            {scope === 'archive' && paidPeriods.isError && (
              <p className={styles.listWarning} role="alert">
                Оплати не завантажилися — підсумки не показуємо
              </p>
            )}
            <PaymentsList
              payments={listedPayments.data}
              categories={categories.data ?? []}
              dueDateOf={dueDateOf}
              valueNoteOf={valueNoteOf}
              isPending={listedPayments.isPending}
              errorMessage={listedPayments.error === null ? null : listedPayments.error.message}
              isArchive={scope === 'archive'}
              onRetry={() => {
                void listedPayments.refetch();
              }}
              onOpen={(id) => {
                void navigate(paymentDetailPath(id));
              }}
            />
          </>
        )}
      </div>
      <Fab
        label="Додати підписку"
        onClick={() => {
          void navigate(ROUTE.paymentCreate);
        }}
      />
    </Screen>
  );
};
